import { normalizeOpaqueLongId } from "@/utils/opaqueLongId"

export const DEFAULT_PACKAGE_COUNTDOWN_REMINDER_MINUTES = 10

const PACKAGE_COUNTDOWN_REMINDER_MESSAGE_TYPE = 10
const TABLE_PAUSE_STATUS = 6

const ACTIVE_FIELD_ALIASES = [
  "isActivateCountdownReminder",
  "isActivatePackageCountdownReminder",
  "isActivatePackageCountdown",
  "isCountdownReminder",
  "isPackageCountdownReminder",
  "packageCountdownReminder",
  "countdownReminderEnabled",
  "packageCountdownReminderEnabled",
  "enableCountdownReminder",
  "enablePackageCountdownReminder",
] as const

const MINUTE_FIELD_ALIASES = [
  "countdownReminderMinutes",
  "countdownReminderMinute",
  "packageCountdownReminderMinutes",
  "packageCountdownReminderMinute",
  "reminderMinutes",
  "remindMinutes",
  "beforeEndReminderMinutes",
  "advanceReminderMinutes",
  "countdownReminderBeforeMinutes",
] as const

const DURATION_FIELD_ALIASES = [
  "packageDurationMinutes",
  "packageDuration",
] as const

type UnknownRecord = Record<string, unknown>

export interface PackageCountdownConfig {
  packageDuration: number
  reminderMinutes: number
  ruleName?: string
  tableBillingRuleId?: string
}

export interface PackageCountdownState extends PackageCountdownConfig {
  status: "normal" | "reminding" | "overtime"
  remainingMinutes: number
  displayText: string
  packageEndTime?: number
}

export interface PackageCountdownTableLike {
  tableName?: string | null
  tableRecordId?: number | null
  status?: number | string | null
  checkIn?: Date | string | null
  preCheckout?: Date | string | null
  preCheckoutTime?: Date | string | null
  preCheckoutFlag?: number | string | boolean | null
  stayMinutes?: number | null
  pauseRecordList?: unknown
  tableBillingFields?: unknown
  packageCountdownInfo?: unknown
  billingSegments?: unknown
  tableRecordFields?: UnknownRecord | null
}

export interface PackageCountdownReminderMessageLike {
  messageContent?: string | null
  messageType?: number | string | null
}

export const buildPackageCountdownReminderSpeech = (tableName: string | null | undefined, remainingMinutes: number) => {
  const displayTableName = tableName?.trim() ? `${tableName.trim()}桌台` : "桌台"
  const displayRemainingMinutes = Math.max(Math.ceil(remainingMinutes), 1)
  return `${displayTableName}还剩${displayRemainingMinutes}分钟结束包段`
}

const isRecord = (value: unknown): value is UnknownRecord => {
  return typeof value === "object" && value !== null
}

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }
  if (typeof value === "string" && value.trim() !== "") {
    const numberValue = Number(value)
    return Number.isFinite(numberValue) ? numberValue : undefined
  }
  return undefined
}

const toActiveFlag = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value
  }
  if (typeof value === "number") {
    return value === 1
  }
  if (typeof value === "string") {
    return value === "1" || value.toLowerCase() === "true"
  }
  return false
}

const parseRuleDetail = (ruleDetail: unknown): UnknownRecord | undefined => {
  if (isRecord(ruleDetail)) {
    return ruleDetail
  }
  if (typeof ruleDetail !== "string" || ruleDetail.trim() === "") {
    return undefined
  }
  try {
    const parsed = JSON.parse(ruleDetail)
    return isRecord(parsed) ? parsed : undefined
  } catch {
    return undefined
  }
}

const firstActiveFlag = (...sources: Array<UnknownRecord | undefined>): boolean | undefined => {
  for (const source of sources) {
    if (!source) continue
    for (const field of ACTIVE_FIELD_ALIASES) {
      if (field in source) {
        return toActiveFlag(source[field])
      }
    }
  }
  return undefined
}

const firstMinuteValue = (...sources: Array<UnknownRecord | undefined>) => {
  for (const source of sources) {
    if (!source) continue
    if (Array.isArray(source.countdownReminders)) {
      const planMinutes = source.countdownReminders
        .filter(isRecord)
        .map(reminder => toFiniteNumber(reminder.minutesBeforeEnd))
        .filter((minutes): minutes is number => minutes !== undefined && minutes >= 0)
      if (planMinutes.length > 0) {
        return Math.max(...planMinutes)
      }
    }
    for (const field of MINUTE_FIELD_ALIASES) {
      const value = toFiniteNumber(source[field])
      if (value !== undefined) {
        return value
      }
    }
  }
  return undefined
}

const firstDurationValue = (...sources: Array<UnknownRecord | undefined>) => {
  for (const source of sources) {
    if (!source) continue
    for (const field of DURATION_FIELD_ALIASES) {
      const value = toFiniteNumber(source[field])
      if (value !== undefined) {
        return value
      }
    }
  }
  return undefined
}

const getStringValue = (source: UnknownRecord | undefined, field: string) => {
  const value = source?.[field]
  return typeof value === "string" && value.trim() !== "" ? value : undefined
}

export const getPackageCountdownConfig = (billingRuleOrDetail?: unknown): PackageCountdownConfig | null => {
  if (!isRecord(billingRuleOrDetail)) {
    return null
  }

  const ruleDetail = parseRuleDetail(billingRuleOrDetail.ruleDetail) ?? billingRuleOrDetail
  const ruleType = toFiniteNumber(ruleDetail.ruleType) ?? toFiniteNumber(billingRuleOrDetail.ruleType)
  if (ruleType !== 2) {
    return null
  }

  const isActive = firstActiveFlag(ruleDetail, billingRuleOrDetail)
  if (!isActive) {
    return null
  }

  const packageDuration = firstDurationValue(ruleDetail, billingRuleOrDetail)
  const reminderMinutes = firstMinuteValue(ruleDetail, billingRuleOrDetail)
  if (!packageDuration || packageDuration <= 0 || reminderMinutes === undefined || reminderMinutes < 0) {
    return null
  }
  if (reminderMinutes > packageDuration) {
    return null
  }

  return {
    packageDuration,
    reminderMinutes,
    ruleName: getStringValue(billingRuleOrDetail, "ruleName"),
    tableBillingRuleId: normalizeOpaqueLongId(billingRuleOrDetail.tableBillingRuleId),
  }
}

const getCheckInTime = (checkIn?: Date | string | null): number | undefined => {
  if (!checkIn) {
    return undefined
  }
  const checkInTime = checkIn instanceof Date ? checkIn.getTime() : new Date(checkIn).getTime()
  return Number.isNaN(checkInTime) ? undefined : checkInTime
}

const toTimestamp = (value: unknown): number | undefined => {
  if (!value) {
    return undefined
  }
  const timestamp = value instanceof Date ? value.getTime() : new Date(String(value)).getTime()
  return Number.isNaN(timestamp) ? undefined : timestamp
}

const toPauseRecords = (value: unknown): UnknownRecord[] => {
  return Array.isArray(value) ? value.filter(isRecord) : []
}

const getPauseStatus = (record: UnknownRecord) => {
  return toFiniteNumber(record.pauseStatus ?? record.pause_status)
}

const getPauseStartTime = (record: UnknownRecord) => {
  return toTimestamp(record.pauseStartTime ?? record.pause_start_time)
}

const getPauseEndTime = (record: UnknownRecord) => {
  return toTimestamp(record.pauseEndTime ?? record.pause_end_time)
}

const isActivePauseRecord = (record: UnknownRecord) => {
  const pauseStatus = getPauseStatus(record)
  if (pauseStatus === 3) {
    return false
  }
  if (pauseStatus === 1) {
    return true
  }
  const pauseStartTime = getPauseStartTime(record)
  const pauseEndTime = getPauseEndTime(record)
  return pauseStartTime !== undefined && (pauseEndTime === undefined || pauseEndTime <= pauseStartTime)
}

export const parsePackageCountdownReminderTableRecordId = (messageContent?: string | null) => {
  if (!messageContent?.trim()) {
    return undefined
  }
  try {
    const parsed = JSON.parse(messageContent)
    if (!isRecord(parsed)) {
      return undefined
    }
    return toFiniteNumber(parsed.tableRecordId ?? parsed.table_record_id)
  } catch {
    return undefined
  }
}

const hasActivePauseRecord = (pauseRecordList: unknown) => {
  return toPauseRecords(pauseRecordList).some(isActivePauseRecord)
}

export const shouldSuppressPackageCountdownReminderMessage = (
  message: PackageCountdownReminderMessageLike,
  tableList: PackageCountdownTableLike[],
) => {
  if (toFiniteNumber(message.messageType) !== PACKAGE_COUNTDOWN_REMINDER_MESSAGE_TYPE) {
    return false
  }
  const tableRecordId = parsePackageCountdownReminderTableRecordId(message.messageContent)
  if (tableRecordId === undefined) {
    return true
  }
  const tableInfo = tableList.find(table => String(table.tableRecordId) === String(tableRecordId))
  if (!tableInfo) {
    return true
  }
  const tableRecordFields = isRecord(tableInfo.tableRecordFields) ? tableInfo.tableRecordFields : undefined
  const tableStatus = toFiniteNumber((tableInfo as UnknownRecord).status ?? tableRecordFields?.status)
  const preCheckoutFlag = tableInfo.preCheckoutFlag ?? toOptionalActiveFlag(tableRecordFields?.preCheckoutFlag)
  return tableStatus === TABLE_PAUSE_STATUS
    || toActiveFlag(preCheckoutFlag)
    || hasActivePauseRecord(tableInfo.pauseRecordList ?? tableRecordFields?.pauseRecordList)
}

const getMergedNonBillingIntervals = (
  pauseRecords: UnknownRecord[],
  extraIntervals: Array<{ end: number, start: number }> = [],
) => {
  const intervals = pauseRecords
    .filter(record => getPauseStatus(record) !== 3)
    .map((record) => {
      const start = getPauseStartTime(record)
      const end = getPauseEndTime(record)
      return start !== undefined && end !== undefined && end > start ? { start, end } : null
    })
    .filter((interval): interval is { end: number, start: number } => interval !== null)
    .concat(extraIntervals.filter(interval => interval.end > interval.start))
    .sort((left, right) => left.start - right.start)

  return intervals.reduce<Array<{ end: number, start: number }>>((merged, interval) => {
    const latest = merged[merged.length - 1]
    if (!latest || interval.start > latest.end) {
      merged.push({ ...interval })
      return merged
    }
    latest.end = Math.max(latest.end, interval.end)
    return merged
  }, [])
}

const getPackageEndTime = (
  checkInTime: number,
  packageDuration: number,
  pauseRecords: UnknownRecord[],
  extraIntervals: Array<{ end: number, start: number }> = [],
) => {
  let packageEndTime = checkInTime + packageDuration * 60 * 1000
  for (const interval of getMergedNonBillingIntervals(pauseRecords, extraIntervals)) {
    if (interval.end <= checkInTime) {
      continue
    }
    const effectiveStart = Math.max(interval.start, checkInTime)
    if (effectiveStart >= packageEndTime) {
      break
    }
    packageEndTime += interval.end - effectiveStart
  }
  return packageEndTime
}

const toOptionalDateLike = (value: unknown): Date | string | null | undefined => {
  return value instanceof Date || typeof value === "string" || value == null ? value : undefined
}

const toOptionalActiveFlag = (value: unknown): boolean | number | string | null | undefined => {
  return typeof value === "boolean" || typeof value === "number" || typeof value === "string" || value == null
    ? value
    : undefined
}

export const getPackageCountdownState = ({
  billingRuleOrDetail,
  checkIn,
  status: tableStatus,
  preCheckoutFlag,
  stayMinutes,
  pauseRecordList,
  now,
}: {
  billingRuleOrDetail?: unknown
  checkIn?: Date | string | null
  status?: number | string | null
  preCheckout?: Date | string | null
  preCheckoutFlag?: number | string | boolean | null
  stayMinutes?: number | null
  pauseRecordList?: unknown
  now: number
}): PackageCountdownState | null => {
  if (toFiniteNumber(tableStatus) === TABLE_PAUSE_STATUS || toActiveFlag(preCheckoutFlag)) {
    return null
  }

  const config = getPackageCountdownConfig(billingRuleOrDetail)
  if (!config) {
    return null
  }

  const checkInTime = getCheckInTime(checkIn)
  const pauseRecords = toPauseRecords(pauseRecordList)
  if (pauseRecords.some(isActivePauseRecord)) {
    return null
  }
  let remainingMinutes: number
  let packageEndTime: number | undefined
  if (checkInTime !== undefined) {
    packageEndTime = getPackageEndTime(checkInTime, config.packageDuration, pauseRecords)
    remainingMinutes = Math.ceil((packageEndTime - now) / (60 * 1000))
  } else if (typeof stayMinutes === "number" && Number.isFinite(stayMinutes)) {
    remainingMinutes = Math.ceil(config.packageDuration - stayMinutes)
  } else {
    return null
  }

  const status = remainingMinutes <= 0
    ? "overtime"
    : remainingMinutes <= config.reminderMinutes
      ? "reminding"
      : "normal"

  return {
    ...config,
    status,
    remainingMinutes: Math.max(remainingMinutes, 0),
    displayText: status === "overtime" ? "包段已超时" : `包段剩余${Math.max(remainingMinutes, 1)}分钟`,
    packageEndTime,
  }
}

export const resolveTablePackageCountdownState = (
  tableInfo: PackageCountdownTableLike | undefined,
  now: number,
): PackageCountdownState | null => {
  if (!tableInfo) {
    return null
  }
  const billingRuleOrDetail = tableInfo.packageCountdownInfo
  const countdownRuleId = isRecord(billingRuleOrDetail)
    ? normalizeOpaqueLongId(billingRuleOrDetail.tableBillingRuleId)
    : undefined
  const segmentStartTime = isRecord(billingRuleOrDetail) && typeof billingRuleOrDetail.segmentStartTime === "string"
    ? billingRuleOrDetail.segmentStartTime.trim()
    : ""
  if (!countdownRuleId || !segmentStartTime || getCheckInTime(segmentStartTime) === undefined) {
    return null
  }
  const tableRecordFields = tableInfo.tableRecordFields
  return getPackageCountdownState({
    billingRuleOrDetail,
    checkIn: segmentStartTime,
    status: tableInfo.status,
    preCheckout: tableInfo.preCheckout ?? tableInfo.preCheckoutTime
      ?? toOptionalDateLike(tableRecordFields?.preCheckout),
    preCheckoutFlag: tableInfo.preCheckoutFlag ?? toOptionalActiveFlag(tableRecordFields?.preCheckoutFlag),
    pauseRecordList: tableInfo.pauseRecordList,
    now,
  })
}
